type PendingEntry = {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
};

let worker: Worker | null = null;
let workerFailed = false;
let nextId = 1;
let inFlight = 0;
const pending = new Map<number, PendingEntry>();

function getWorker() {
  if (workerFailed) return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL('./worker/network-worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (event) => {
      const { id, error, ...rest } = event.data ?? {};
      const entry = pending.get(id);
      if (!entry) return;
      pending.delete(id);
      inFlight = Math.max(0, inFlight - 1);
      if (error) entry.reject(new Error(error));
      else entry.resolve(rest);
    };
    worker.onerror = () => {
      workerFailed = true;
      worker = null;
      for (const [, entry] of pending) {
        entry.reject(new Error('Network worker failed'));
      }
      pending.clear();
      inFlight = 0;
    };
    return worker;
  } catch {
    workerFailed = true;
    return null;
  }
}

function resolveFetchUrl(url: string) {
  if (!url || typeof url !== 'string') return url;
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return url;
  return new URL(url, `${window.location.origin}/`).href;
}

function postToWorker(type: string, url: string, init: RequestInit) {
  const w = getWorker();
  if (!w) return Promise.reject(new Error('Worker unavailable'));

  const id = nextId++;
  inFlight += 1;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    w.postMessage({ id, type, url: resolveFetchUrl(url), init });
  });
}

export function getNetworkInFlightCount() {
  return inFlight;
}

function isHtmlPayload(text: string, contentType = '') {
  const ct = contentType.toLowerCase();
  if (ct.includes('text/html')) return true;
  const trimmed = text.trimStart();
  return trimmed.startsWith('<!') || trimmed.startsWith('<html');
}

function stripJsonBom(text: string) {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

function parseJsonText(text: string, url: string, contentType = '') {
  if (isHtmlPayload(text, contentType)) {
    throw new Error(`Not JSON (${url})`);
  }
  const payload = stripJsonBom(text);
  try {
    return JSON.parse(payload);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Invalid JSON (${url}): ${message}`);
  }
}

async function fetchJsonOnMainThread(url: string, init: RequestInit = {}) {
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`HTTP ${res.status} ${url}`);
  const contentType = res.headers.get('content-type') || '';
  const text = await res.text();
  return parseJsonText(text, url, contentType);
}

function shouldFallbackFromWorkerError(err: unknown) {
  const msg = err instanceof Error ? err.message : String(err);
  return msg.includes('Worker unavailable') || msg.includes('Network worker failed');
}

export async function workerFetch(url: string, init: RequestInit = {}) {
  const resolved = resolveFetchUrl(url);
  try {
    const result = (await postToWorker('fetch', resolved, init)) as {
      ok: boolean;
      status: number;
      statusText: string;
      headers: Record<string, string>;
      body: ArrayBuffer;
    };
    const contentType = result.headers?.['content-type'] || '';
    return {
      ok: result.ok,
      status: result.status,
      statusText: result.statusText,
      headers: result.headers,
      async json() {
        const text = new TextDecoder().decode(result.body);
        return parseJsonText(text, resolved, contentType);
      },
      async arrayBuffer() {
        return result.body;
      },
      async text() {
        return new TextDecoder().decode(result.body);
      },
    };
  } catch {
    return fetch(resolved, init);
  }
}

export async function workerFetchJson<T = unknown>(url: string, init: RequestInit = {}) {
  const resolved = resolveFetchUrl(url);
  try {
    const result = (await postToWorker('json', resolved, init)) as { data: T };
    return result.data;
  } catch (err) {
    if (!shouldFallbackFromWorkerError(err)) throw err;
    return fetchJsonOnMainThread(resolved, init) as Promise<T>;
  }
}
